import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DnsIcon from '@mui/icons-material/Dns';
import WebIcon from '@mui/icons-material/Web';
import api, { apiBaseURL } from '../../services/api';
import { APP_VERSION, APP_RELEASE_DATE } from '../../version';
import { SMART_COLORS as COLORS, smartCardSx } from '../../theme/smartStableTheme';

const TIMEZONES = [
  'Asia/Riyadh',
  'Asia/Dubai',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'UTC',
];

const DATE_FORMATS = ['yyyy-MM-dd', 'dd/MM/yyyy', 'MM/dd/yyyy', 'dd-MM-yyyy'];

const DEFAULT_SETTINGS = {
  system_name: 'Smart Stable',
  timezone: 'Asia/Riyadh',
  default_language: 'en',
  date_format: 'yyyy-MM-dd',
  stable_owner_id: '',
  logo: '',
};

const resolveAssetUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiBaseURL}${value.startsWith('/') ? '' : '/'}${value}`;
};

const formatOwnerName = (owner) => {
  if (!owner) return '';
  const name = [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim();
  return name || owner.owner_name || `#${owner.owner_id || owner.id}`;
};

const formatTimezoneNow = (timezone, language) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'Asia/Riyadh',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date()).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.year}/${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`;
  } catch {
    const fallback = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    return `${fallback.getFullYear()}/${pad(fallback.getMonth() + 1)}/${pad(fallback.getDate())} ${pad(fallback.getHours())}:${pad(fallback.getMinutes())}`;
  }
};

const SystemSettings = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'pt' ? 'pt' : 'en';
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [owners, setOwners] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [backendInfo, setBackendInfo] = useState(null);

  const hasChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [initialSettings, settings]
  );

  const currentTime = useMemo(
    () => formatTimezoneNow(settings.timezone, lang),
    [lang, settings.timezone]
  );

  const selectedStableOwner = useMemo(
    () =>
      owners.find(
        (owner) => String(owner.owner_id || owner.id) === String(settings.stable_owner_id || '')
      ) || null,
    [owners, settings.stable_owner_id]
  );

  useEffect(() => {
    let alive = true;

    const fetchSettings = async () => {
      setLoading(true);
      setError('');
      try {
        const [settingsResponse, ownersResponse, versionResponse] = await Promise.all([
          api.get('/system-settings'),
          api.get('/owners').catch(() => ({ data: [] })),
          api.get('/version').catch(() => ({ data: null })),
        ]);
        const ownersList = Array.isArray(ownersResponse.data) ? ownersResponse.data : [];
        const data = settingsResponse.data || {};
        const next = {
          ...DEFAULT_SETTINGS,
          ...data,
          stable_owner_id: data.stable_owner_id == null ? '' : String(data.stable_owner_id),
        };
        if (!alive) return;
        setOwners(ownersList);
        setSettings(next);
        setInitialSettings(next);
        setBackendInfo(versionResponse?.data || null);
        document.title = next.system_name || DEFAULT_SETTINGS.system_name;
      } catch {
        if (!alive) return;
        setError(t('fetch_system_settings_error'));
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchSettings();
    return () => {
      alive = false;
    };
  }, [t]);

  const updateField = (field) => (event) => {
    setSettings((previous) => ({ ...previous, [field]: event.target.value }));
    setSuccess('');
    setError('');
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingLogo(true);
    setSuccess('');
    setError('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await api.post('/system-settings/logo', formData);
      setSettings((previous) => ({ ...previous, logo: data.logo || '' }));
      setInitialSettings((previous) => ({ ...previous, logo: data.logo || '' }));
      setSuccess(t('logo_upload_success'));
    } catch {
      setError(t('logo_upload_error'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleReset = () => {
    setSettings(initialSettings);
    setSuccess('');
    setError('');
  };

  const handleSave = async () => {
    const cleanName = settings.system_name.trim();
    if (!cleanName) {
      setError(t('system_name_required'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...settings,
        system_name: cleanName,
        stable_owner_id: settings.stable_owner_id || null,
      };
      const { data } = await api.put('/system-settings', payload);
      const next = {
        ...DEFAULT_SETTINGS,
        ...data,
        stable_owner_id: data.stable_owner_id == null ? '' : String(data.stable_owner_id),
      };
      setSettings(next);
      setInitialSettings(next);
      document.title = next.system_name;
      setSuccess(t('system_settings_update_success'));
    } catch {
      setError(t('system_settings_update_error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        className="smart-page-container"
        sx={{
          bgcolor: COLORS.page,
          minHeight: '100vh',
          width: '100%',
          maxWidth: 'var(--app-page-max)',
          mx: 'auto',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={26} />
          <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>
            {t('loading')}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      dir={dir}
      className="smart-page-container"
      sx={{
        bgcolor: COLORS.page,
        minHeight: '100vh',
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            ...smartCardSx,
            p: { xs: 2.2, md: 3 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  bgcolor: COLORS.goldSoft,
                  color: COLORS.gold,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <SettingsIcon />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {t('system_settings')}
                </Typography>
                <Typography sx={{ color: COLORS.muted, mt: 0.4 }}>
                  {t('system_settings_hint')}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                disabled={!hasChanges || saving}
                onClick={handleReset}
                sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
              >
                {t('reset')}
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <SaveIcon />}
                disabled={!hasChanges || saving}
                onClick={handleSave}
                sx={{
                  bgcolor: COLORS.gold,
                  color: '#fff',
                  fontWeight: 900,
                  '&:hover': { bgcolor: COLORS.goldHover },
                }}
              >
                {t('save_changes')}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.4fr 0.8fr' },
            gap: 2.5,
            width: '100%',
          }}
        >
          <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2, md: 2.5 } }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 0.5 }}>
              {t('general_settings')}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mb: 2.5 }}>
              {t('general_settings_hint')}
            </Typography>

            <Stack spacing={2.2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '96px 1fr' },
                  gap: 2,
                  alignItems: 'center',
                  p: 2,
                  border: `1px solid ${COLORS.line}`,
                  bgcolor: COLORS.cardSoft,
                }}
              >
                <Avatar
                  src={resolveAssetUrl(settings.logo)}
                  variant="rounded"
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: COLORS.goldSoft,
                    color: COLORS.gold,
                    fontWeight: 900,
                  }}
                >
                  {(settings.system_name || DEFAULT_SETTINGS.system_name).slice(0, 1)}
                </Avatar>
                <Box>
                  <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                    {t('stable_logo')}
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 13, mb: 1.2 }}>
                    {t('stable_logo_hint')}
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={uploadingLogo ? <CircularProgress size={16} /> : <UploadFileIcon />}
                    disabled={uploadingLogo}
                    sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
                  >
                    {t('upload_logo')}
                    <input hidden accept="image/png,image/jpeg,image/webp" type="file" onChange={handleLogoUpload} />
                  </Button>
                </Box>
              </Box>

              <TextField
                label={t('system_name')}
                value={settings.system_name}
                onChange={updateField('system_name')}
                fullWidth
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  label={t('timezone')}
                  value={settings.timezone}
                  onChange={updateField('timezone')}
                  fullWidth
                >
                  {TIMEZONES.map((zone) => (
                    <MenuItem key={zone} value={zone}>
                      {zone}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t('default_language')}
                  value={settings.default_language}
                  onChange={updateField('default_language')}
                  fullWidth
                >
                  <MenuItem value="ar">العربية</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="pt">Português</MenuItem>
                </TextField>
              </Stack>

              <TextField
                select
                label={t('date_format')}
                value={settings.date_format}
                onChange={updateField('date_format')}
                fullWidth
              >
                {DATE_FORMATS.map((format) => (
                  <MenuItem key={format} value={format}>
                    {format}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t('stable_owner_for_billing')}
                value={settings.stable_owner_id}
                onChange={updateField('stable_owner_id')}
                helperText={t('stable_owner_for_billing_hint')}
                fullWidth
              >
                <MenuItem value="">{t('no_stable_owner_selected')}</MenuItem>
                {owners.map((owner) => {
                  const ownerId = owner.owner_id || owner.id;
                  return (
                    <MenuItem key={ownerId} value={String(ownerId)}>
                      {formatOwnerName(owner)} ({owner.horses_count || 0})
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2, md: 2.5 } }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 0.5 }}>
              {t('settings_preview')}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mb: 2.5 }}>
              {t('settings_preview_hint')}
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: COLORS.cardSoft }}>
                <Avatar
                  src={resolveAssetUrl(settings.logo)}
                  variant="rounded"
                  sx={{ width: 72, height: 72, mb: 1.5, bgcolor: COLORS.goldSoft, color: COLORS.gold }}
                >
                  {(settings.system_name || DEFAULT_SETTINGS.system_name).slice(0, 1)}
                </Avatar>
                <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
                  {t('system_name')}
                </Typography>
                <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, mt: 0.5 }}>
                  {settings.system_name || DEFAULT_SETTINGS.system_name}
                </Typography>
              </Box>

              <Divider />

              <Stack spacing={1.3}>
                <Chip
                  icon={<AccessTimeIcon />}
                  label={<>{t('current_time')}: <span className="date-ltr">{currentTime}</span></>}
                  sx={{
                    justifyContent: 'flex-start',
                    bgcolor: COLORS.blueSoft,
                    color: COLORS.text,
                    fontWeight: 800,
                    height: 'auto',
                    py: 1,
                  }}
                />
                <Chip
                  icon={<LanguageIcon />}
                  label={`${t('default_language')}: ${settings.default_language.toUpperCase()}`}
                  sx={{
                    justifyContent: 'flex-start',
                    bgcolor: COLORS.greenSoft,
                    color: COLORS.text,
                    fontWeight: 800,
                    height: 'auto',
                    py: 1,
                  }}
                />
                <Chip
                  label={`${t('stable_owner_for_billing')}: ${
                    selectedStableOwner
                      ? formatOwnerName(selectedStableOwner)
                      : t('no_stable_owner_selected')
                  }`}
                  sx={{
                    justifyContent: 'flex-start',
                    bgcolor: COLORS.goldSoft,
                    color: COLORS.text,
                    fontWeight: 800,
                    height: 'auto',
                    py: 1,
                  }}
                />
              </Stack>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t('timezone_hint')}
              </Alert>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
                  {t('version_info')}
                </Typography>
                <Stack spacing={1.3} sx={{ mt: 1 }}>
                  <Chip
                    icon={<WebIcon />}
                    label={`${t('frontend_version')}: v${APP_VERSION}${
                      APP_RELEASE_DATE ? ` (${APP_RELEASE_DATE})` : ''
                    }`}
                    sx={{
                      justifyContent: 'flex-start',
                      bgcolor: COLORS.blueSoft,
                      color: COLORS.text,
                      fontWeight: 800,
                      height: 'auto',
                      py: 1,
                    }}
                  />
                  <Chip
                    icon={<DnsIcon />}
                    label={`${t('backend_version')}: ${
                      backendInfo?.version ? `v${backendInfo.version}` : t('version_unavailable')
                    }${backendInfo?.releaseDate ? ` (${backendInfo.releaseDate})` : ''}${
                      backendInfo?.environment ? ` · ${backendInfo.environment}` : ''
                    }`}
                    sx={{
                      justifyContent: 'flex-start',
                      bgcolor: COLORS.goldSoft,
                      color: COLORS.text,
                      fontWeight: 800,
                      height: 'auto',
                      py: 1,
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default SystemSettings;
